import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type Point, clampToBounds, panelPositionKey, pointSchema } from '../lib/draggable';
import { clearPersisted, readPersisted, writePersisted } from '../lib/persisted';
import styles from './FloatingPanel.module.css';

/** How far one arrow-key press moves the panel. */
const NUDGE = 16;

/** Below this the panel docks as a sheet and dragging is turned off. */
const DRAG_BREAKPOINT = '(min-width: 721px)';

/**
 * Whether this viewport is one where a panel can usefully be moved. Tolerant of
 * an environment without `matchMedia` — jsdom, notably — where it assumes yes.
 */
function canDragHere(): boolean {
  if (typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(DRAG_BREAKPOINT).matches;
}

export interface FloatingPanelProps {
  /** Stable id — the stored position is keyed on it. */
  id: string;
  title: string;
  /** Where the panel sits until someone moves it. Any CSS length. */
  dock: Pick<CSSProperties, 'top' | 'right' | 'bottom' | 'left'>;
  defaultCollapsed?: boolean;
  children: ReactNode;
}

/**
 * A panel that floats over the map and can be dragged anywhere on it.
 *
 * Fixed chrome on a map is always covering something: the storm you're looking
 * at, or the coastline you're using to place it. Rather than search for one
 * position that's never in the way — there isn't one — the panel docks
 * somewhere reasonable and then belongs to the viewer, who can put it wherever
 * their weather isn't. Where it ends up is remembered per browser.
 *
 * Dragging is pointer-based (so it works with touch and pen), and the handle is
 * a real button that arrow keys nudge, so moving the panel is not a
 * mouse-only capability.
 */
export function FloatingPanel({
  id,
  title,
  dock,
  defaultCollapsed = false,
  children,
}: FloatingPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Point | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  // Collapsed to a title bar on a phone, where the panel docks as a sheet and
  // an expanded one would cover most of the map it describes.
  const [isCollapsed, setIsCollapsed] = useState(() => defaultCollapsed || !canDragHere());
  const [canDrag, setCanDrag] = useState(false);

  const storageKey = panelPositionKey(id);

  // Dragging is a pointer-and-space affordance. On a phone the panel docks as a
  // sheet instead, so there is nothing to drag and nothing to lose off-screen.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia(DRAG_BREAKPOINT);
    const sync = () => setCanDrag(query.matches);

    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  /** Measures the panel against its container. Null until both exist. */
  const measure = useCallback(() => {
    const panel = panelRef.current;
    const container = panel?.offsetParent;
    if (!panel || !container) return null;

    const panelRect = panel.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return { panelRect, containerRect };
  }, []);

  // Restore the stored position, clamped: it was chosen in a window that may
  // have been much larger than this one.
  useEffect(() => {
    if (!canDrag) {
      setPosition(null);
      return;
    }

    const stored = readPersisted(storageKey, pointSchema.nullable(), null);
    if (!stored) return;

    const rects = measure();
    if (!rects) {
      setPosition(stored);
      return;
    }

    setPosition(
      clampToBounds(
        stored,
        { width: rects.panelRect.width, height: rects.panelRect.height },
        { width: rects.containerRect.width, height: rects.containerRect.height },
      ),
    );
  }, [canDrag, measure, storageKey]);

  const moveTo = useCallback(
    (next: Point, persist: boolean) => {
      const rects = measure();
      const clamped = rects
        ? clampToBounds(
            next,
            { width: rects.panelRect.width, height: rects.panelRect.height },
            { width: rects.containerRect.width, height: rects.containerRect.height },
          )
        : next;

      setPosition(clamped);
      if (persist) writePersisted(storageKey, clamped);
    },
    [measure, storageKey],
  );

  // A window that shrinks can strand the panel outside the map.
  useEffect(() => {
    if (!canDrag || !position) return;

    const onResize = () => moveTo(position, true);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [canDrag, moveTo, position]);

  const drag = useRef<{ pointerId: number; offsetX: number; offsetY: number } | null>(null);

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    // Primary button only; a right-click on the header shouldn't grab it.
    if (!canDrag || event.button !== 0) return;

    const rects = measure();
    if (!rects) return;

    drag.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rects.panelRect.left,
      offsetY: event.clientY - rects.panelRect.top,
    };

    // Capture on the handle so a fast drag that outruns the cursor still tracks.
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;

    const rects = measure();
    if (!rects) return;

    moveTo(
      {
        x: event.clientX - state.offsetX - rects.containerRect.left,
        y: event.clientY - state.offsetY - rects.containerRect.top,
      },
      false,
    );
  };

  const endDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;

    drag.current = null;
    setIsDragging(false);
    // Persist the resting place, not every frame of the drag.
    if (position) writePersisted(storageKey, position);
  };

  const reset = () => {
    clearPersisted(storageKey);
    setPosition(null);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!canDrag) return;

    // Home is the keyboard equivalent of the reset button in the header.
    if (event.key === 'Home') {
      event.preventDefault();
      reset();
      return;
    }

    const delta = NUDGE_KEYS[event.key];
    if (!delta) return;

    event.preventDefault();
    // The page listens for arrow keys to step radar frames. While the handle
    // has focus they belong to the panel.
    event.stopPropagation();

    const rects = measure();
    if (!rects) return;

    // The panel may still be docked, in which case its current px position is
    // whatever CSS put it at.
    const from = position ?? {
      x: rects.panelRect.left - rects.containerRect.left,
      y: rects.panelRect.top - rects.containerRect.top,
    };

    moveTo({ x: from.x + delta.x, y: from.y + delta.y }, true);
  };

  const style: CSSProperties =
    canDrag && position
      ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
      : dock;

  return (
    <section
      ref={panelRef}
      className={styles.panel}
      style={style}
      data-dragging={isDragging}
      data-collapsed={isCollapsed}
      aria-label={title}
    >
      <div className={styles.header}>
        <button
          type="button"
          className={styles.handle}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
          aria-label={`Move the ${title} panel — arrow keys nudge it`}
        >
          <GripIcon />
          <span className={styles.title}>{title}</span>
        </button>

        {position && (
          <button
            type="button"
            className={styles.action}
            onClick={reset}
            title="Return the panel to its default position"
            aria-label={`Return the ${title} panel to its default position`}
          >
            <ResetIcon />
          </button>
        )}

        <button
          type="button"
          className={styles.action}
          onClick={() => setIsCollapsed((collapsed) => !collapsed)}
          aria-expanded={!isCollapsed}
          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} the ${title} panel`}
        >
          <ChevronIcon />
        </button>
      </div>

      <div className={styles.body}>{children}</div>
    </section>
  );
}

const NUDGE_KEYS: Record<string, Point | undefined> = {
  ArrowLeft: { x: -NUDGE, y: 0 },
  ArrowRight: { x: NUDGE, y: 0 },
  ArrowUp: { x: 0, y: -NUDGE },
  ArrowDown: { x: 0, y: NUDGE },
};

function GripIcon() {
  return (
    <svg
      viewBox="0 0 10 14"
      width="8"
      height="12"
      fill="currentColor"
      className={styles.grip}
      aria-hidden="true"
    >
      <circle cx="2" cy="2" r="1.1" />
      <circle cx="8" cy="2" r="1.1" />
      <circle cx="2" cy="7" r="1.1" />
      <circle cx="8" cy="7" r="1.1" />
      <circle cx="2" cy="12" r="1.1" />
      <circle cx="8" cy="12" r="1.1" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.chevron}
      aria-hidden="true"
    >
      <path d="M4 6.5 8 10.5 12 6.5" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8a5 5 0 1 0 1.6-3.7" />
      <path d="M3 2.5V5h2.5" />
    </svg>
  );
}
